// src/backendServices/adminManagment/api_creatorAccount.ts
//
// Panneau « Mon compte » du créateur (2026-10-29). admin-api, CreatorAccountController :
// contrôleur Symfony pur, liste blanche de champs. L'adresse e-mail est en lecture seule.
// Le changement de mot de passe exige l'ancien.

import { API_URLS } from '../urls';

export interface CreatorAccount {
  id              : number;
  email           : string;   // lecture seule
  firstname       : string | null;
  lastname        : string | null;
  pseudonym       : string | null;
  slug            : string | null;
  biography       : string | null;
  personalPageUrl : string | null;
  address         : string | null;
  zipCode         : string | null;
  city            : string | null;
  region          : string | null;
  country         : string | null;
  reporterAvailable : boolean;       // accepte d'être sollicité comme reporter Kleon
  reporterCity      : string | null; // ville de départ
  reporterRadiusKm  : number | null; // rayon de déplacement, en km
  verified        : boolean;
  accountStatus   : string;
  creationDate    : string | null;
  lastLoginDate   ?: string | null;
  lastLoginCity   ?: string | null;
  lastLoginCountry?: string | null;
  quotaDisk       : number;
  quotaUsed       : number;
  nbDevicesMax    : number;
  nbDevicesUsed   : number;
  nbChannelsMax   : number;
  nbChannelsUsed  : number;
  durationMaxBroadcasting     : number;
  durationElapsedBroadcasting : number;
  durationMaxRecording        : number;
  durationElapsedRecording    : number;
}

/** Les champs que le créateur peut modifier lui-même. */
export type CreatorAccountUpdate = Partial<Pick<CreatorAccount,
  'firstname' | 'lastname' | 'pseudonym' | 'slug' | 'biography' | 'personalPageUrl' |
  'address' | 'zipCode' | 'city' | 'region' | 'country' |
  'reporterAvailable' | 'reporterCity' | 'reporterRadiusKm'
>>;

export class CreatorAccountError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

const call = async <T>(url: string, init?: RequestInit): Promise<T> => {
  const res  = await fetch(url, { cache: 'no-store', ...init });
  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new CreatorAccountError(data.error ?? `HTTP ${res.status}`, res.status);

  return data as T;
};

const post = (body: unknown): RequestInit => ({
  method : 'POST',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  body   : JSON.stringify(body),
});

export const fetchCreatorAccount = (adminId: number): Promise<CreatorAccount> =>
  call<CreatorAccount>(API_URLS.creatorAccount.replace('{adminId}', `${adminId}`));

export const updateCreatorAccount = (adminId: number, changes: CreatorAccountUpdate): Promise<CreatorAccount> =>
  call<CreatorAccount>(API_URLS.creatorAccount.replace('{adminId}', `${adminId}`), post(changes));

export const changeCreatorPassword = (adminId: number, currentPassword: string, newPassword: string): Promise<{ ok: boolean }> =>
  call<{ ok: boolean }>(API_URLS.creatorAccountPassword.replace('{adminId}', `${adminId}`), post({ currentPassword, newPassword }));

/** Crédits Kléon : 10 par tranche de 10 min de direct terminée + 10 par like sur ses vidéos. */
export interface CreatorCredits {
  credits          : number;
  broadcastCredits : number;
  likeCredits      : number;
  viewingCredits   : number;
  viewingSeconds   : number;
  broadcastSeconds : number;
  likes            : number;
  rates            : { blockSeconds: number; creditsPerBlock: number; creditsPerLike: number };
}

export const fetchCreatorCredits = (adminId: number): Promise<CreatorCredits> =>
  call<CreatorCredits>(API_URLS.creatorAccountCredits.replace('{adminId}', `${adminId}`));
